import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, DotsSixVertical, CurrencyDollar, Calendar, User, ArrowRight, Funnel, ChartLine, Target, Bell, Download, MagnifyingGlass, FileText, Handshake, CheckCircle, XCircle, ClockCounterClockwise } from '@phosphor-icons/react';
import { CurrencyInput } from '../components/CurrencyInput';
import { PageHero } from '../components/motion/PageHero';
import { getOpportunities, createOpportunity, updateOpportunity, getOrganizations, getContacts, exportOpportunities, getOpportunityHistory } from '../services/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../utils/apiError';
import { AIAssistButton } from '../components/AIAssistButton';

const pipelineStages = [
  { id: 'lead_entrant', label: 'Lead entrant', color: '#62A8D4', icon: MagnifyingGlass },
  { id: 'qualification', label: 'Qualification', color: '#E8A365', icon: Funnel },
  { id: 'proposition', label: 'Proposition', color: '#8DB0A1', icon: FileText },
  { id: 'relance', label: 'Relance', color: '#D47A62', icon: Bell },
  { id: 'accord_principe', label: 'Accord de principe', color: '#2D5A4C', icon: Handshake },
  { id: 'converti', label: 'Converti', color: '#1E4136', icon: CheckCircle },
  { id: 'perdu', label: 'Perdu', color: '#5E6D66', icon: XCircle },
];

// Rendu d'une option d'étape avec icône + pastille
const StageLabel = ({ stage }) => {
  if (!stage) return null;
  const Icon = stage.icon;
  return (
    <span className="flex items-center gap-2">
      <Icon size={14} style={{ color: stage.color }} />
      <span>{stage.label}</span>
    </span>
  );
};

const typeBesoinOptions = [
  { value: 'adhesion', label: 'Adhésion FTPE' },
  { value: 'adhesion_oscop_ge', label: "Adhésion O'SCOP G.E" },
  { value: 'mission_cipaesse', label: 'Mission coopérative C.I.P.A.E.S.S.E' },
  { value: 'mission_cipascop', label: 'Mission coopérative C.I.P.A.S.C.O.P' },
  { value: 'accompagnement', label: 'Accompagnement' },
  { value: 'dispositif_ess', label: 'Dispositif ESS' },
  { value: 'formation', label: 'Formation' },
  { value: 'partenariat', label: 'Partenariat' },
  { value: 'autre', label: 'Autre' },
];

const motifPerteOptions = [
  { value: 'pas_budget', label: 'Pas de budget' },
  { value: 'non_prioritaire', label: 'Non prioritaire' },
  { value: 'hors_cible', label: 'Hors cible' },
  { value: 'dossier_incomplet', label: 'Dossier trop incomplet' },
  { value: 'pas_reponse', label: 'Pas de réponse' },
  { value: 'refus_conditions', label: 'Refus des conditions' },
  { value: 'risque_eleve', label: 'Risque trop élevé' },
  { value: 'abandon', label: 'Abandon porteur' },
];

// Seuil (jours) au-delà duquel une opportunité active est considérée à relancer
const STALE_DAYS = 30;
const isStaleOpp = (opp) => {
  if (!opp || opp.pipeline_stage === 'converti' || opp.pipeline_stage === 'perdu') return false;
  const ref = opp.updated_at || opp.created_at;
  if (!ref) return false;
  const days = (Date.now() - new Date(ref).getTime()) / (1000 * 60 * 60 * 24);
  return days >= STALE_DAYS;
};
const daysSince = (iso) => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
};

export function OpportunitiesPage() {
  const [opportunities, setOpportunities] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOpp, setEditingOpp] = useState(null);
  const [lostDialogOpen, setLostDialogOpen] = useState(false);
  const [selectedOppForLost, setSelectedOppForLost] = useState(null);
  const [lostReason, setLostReason] = useState('');
  const [formData, setFormData] = useState({
    titre: '',
    organization_id: '',
    contact_id: '',
    type_besoin: '',
    montant_estime: '',
    probabilite_conversion: '',
    notes_qualification: '',
    pipeline_stage: 'lead_entrant',
  });

  // Filtres
  const [filterTypeBesoin, setFilterTypeBesoin] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterMontantMin, setFilterMontantMin] = useState('');
  const [filterMontantMax, setFilterMontantMax] = useState('');
  const [filterHistorySearch, setFilterHistorySearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Historique de l'opportunité en édition
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Dialog note changement d'étape
  const [stageChangeOpen, setStageChangeOpen] = useState(false);
  const [stageChangeState, setStageChangeState] = useState(null); // {opp, fromStage, toStage}
  const [stageChangeNote, setStageChangeNote] = useState('');

  const fetchData = async () => {
    try {
      const params = {};
      if (filterTypeBesoin && filterTypeBesoin !== 'all') params.type_besoin = filterTypeBesoin;
      if (filterStage && filterStage !== 'all') params.pipeline_stage = filterStage;
      if (filterMontantMin) params.montant_min = parseFloat(filterMontantMin);
      if (filterMontantMax) params.montant_max = parseFloat(filterMontantMax);
      if (filterHistorySearch) params.history_search = filterHistorySearch;
      const [oppsRes, orgsRes, contactsRes] = await Promise.all([
        getOpportunities(params),
        getOrganizations(),
        getContacts()
      ]);
      setOpportunities(oppsRes.data.items || []);
      setOrganizations(orgsRes.data.items || []);
      setContacts(contactsRes.data.items || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterTypeBesoin, filterStage, filterMontantMin, filterMontantMax, filterHistorySearch]);

  const resetFilters = () => {
    setFilterTypeBesoin(''); setFilterStage(''); setFilterMontantMin(''); setFilterMontantMax(''); setFilterHistorySearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        montant_estime: formData.montant_estime ? parseFloat(formData.montant_estime) : null,
        probabilite_conversion: formData.probabilite_conversion ? parseInt(formData.probabilite_conversion) : null,
      };
      
      if (editingOpp) {
        await updateOpportunity(editingOpp.id, data);
        toast.success('Opportunité mise à jour');
      } else {
        await createOpportunity(data);
        toast.success('Opportunité créée');
      }
      setDialogOpen(false);
      setEditingOpp(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Erreur'));
    }
  };

  const resetForm = () => {
    setFormData({
      titre: '',
      organization_id: '',
      contact_id: '',
      type_besoin: '',
      montant_estime: '',
      probabilite_conversion: '',
      notes_qualification: '',
      pipeline_stage: 'lead_entrant',
    });
  };

  const handleDragStart = (e, opp) => {
    e.dataTransfer.setData('oppId', opp.id);
  };

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    const oppId = e.dataTransfer.getData('oppId');
    const opp = opportunities.find(o => o.id === oppId);
    if (!opp || opp.pipeline_stage === newStage) return;

    if (newStage === 'perdu') {
      setSelectedOppForLost(opp);
      setLostDialogOpen(true);
      return;
    }

    // Ouvrir le dialogue de note
    setStageChangeState({ opp, fromStage: opp.pipeline_stage, toStage: newStage });
    setStageChangeNote('');
    setStageChangeOpen(true);
  };

  const handleStageChangeConfirm = async () => {
    if (!stageChangeState) return;
    try {
      const payload = { pipeline_stage: stageChangeState.toStage };
      if (stageChangeNote.trim()) payload.stage_change_note = stageChangeNote.trim();
      await updateOpportunity(stageChangeState.opp.id, payload);
      toast.success('Opportunité déplacée');
      setStageChangeOpen(false);
      setStageChangeState(null);
      setStageChangeNote('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du déplacement');
    }
  };

  const handleLostConfirm = async () => {
    if (!selectedOppForLost || !lostReason) return;

    try {
      const payload = {
        pipeline_stage: 'perdu',
        statut: 'perdu',
        motif_perte: lostReason,
      };
      if (stageChangeNote.trim()) payload.stage_change_note = stageChangeNote.trim();
      await updateOpportunity(selectedOppForLost.id, payload);
      toast.success('Opportunité marquée comme perdue');
      setLostDialogOpen(false);
      setSelectedOppForLost(null);
      setLostReason('');
      setStageChangeNote('');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const openNewDialog = () => {
    setEditingOpp(null);
    resetForm();
    setDialogOpen(true);
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (filterTypeBesoin && filterTypeBesoin !== 'all') params.type_besoin = filterTypeBesoin;
      if (filterStage && filterStage !== 'all') params.pipeline_stage = filterStage;
      if (filterMontantMin) params.montant_min = parseFloat(filterMontantMin);
      if (filterMontantMax) params.montant_max = parseFloat(filterMontantMax);
      const res = await exportOpportunities(params);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `opportunites_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch (e) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleCardClick = (opp) => {    setEditingOpp(opp);
    setFormData({
      titre: opp.titre || '',
      organization_id: opp.organization_id || '',
      contact_id: opp.contact_id || '',
      type_besoin: opp.type_besoin || '',
      montant_estime: opp.montant_estime?.toString() || '',
      probabilite_conversion: opp.probabilite_conversion?.toString() || '',
      notes_qualification: opp.notes_qualification || '',
      pipeline_stage: opp.pipeline_stage || 'lead_entrant',
    });
    setDialogOpen(true);
    // Charger l'historique des changements d'étape
    setHistory([]);
    setHistoryLoading(true);
    getOpportunityHistory(opp.id)
      .then((r) => setHistory(r.data.items || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  };

  const getOrgName = (id) => organizations.find(o => o.id === id)?.raison_sociale || '';
  const getContactName = (id) => {
    const c = contacts.find(c => c.id === id);
    return c ? `${c.prenom} ${c.nom}` : '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Opportunités"
        subtitle="Pipeline commercial ESS — de la découverte à la conversion"
        icon={<Target size={24} weight="duotone" className="text-white" />}
        gradient="from-[#FF8E00] to-[#EA3C3A]"
        accents={['#D5007D', '#00AEEF']}
        actions={(
          <>
            <Button variant="outline" onClick={() => setShowFilters(v => !v)} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="opp-filters-btn">
              <Funnel size={18} className="mr-2" /> Filtres
            </Button>
            <Button variant="outline" onClick={handleExport} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="export-opp-btn">
              <Download size={18} className="mr-2" /> Exporter
            </Button>
            <Button onClick={openNewDialog} className="bg-white text-[#EA3C3A] hover:bg-white/90 font-bold" data-testid="new-opp-btn">
              <Plus size={18} className="mr-2" />
              Nouvelle
            </Button>
          </>
        )}
      />

      {showFilters && (
        <Card className="border-[#E8E6E1]"><CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <Select value={filterTypeBesoin} onValueChange={setFilterTypeBesoin}>
            <SelectTrigger className="w-[220px]" data-testid="filter-opp-besoin"><SelectValue placeholder="Type de besoin" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {typeBesoinOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStage} onValueChange={setFilterStage}>
            <SelectTrigger className="w-[200px]" data-testid="filter-opp-stage">
              <SelectValue placeholder="Étape pipeline">
                {filterStage && filterStage !== 'all' ? <StageLabel stage={pipelineStages.find(s => s.id === filterStage)} /> : undefined}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {pipelineStages.map(s => <SelectItem key={s.id} value={s.id} data-testid={`filter-opp-stage-${s.id}`}><StageLabel stage={s} /></SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Input type="number" placeholder="Montant min €" value={filterMontantMin} onChange={(e) => setFilterMontantMin(e.target.value)} className="w-[140px]" data-testid="filter-opp-min" />
            <span className="text-[#5E6D66]">–</span>
            <Input type="number" placeholder="Montant max €" value={filterMontantMax} onChange={(e) => setFilterMontantMax(e.target.value)} className="w-[140px]" data-testid="filter-opp-max" />
          </div>
          <div className="relative">
            <ClockCounterClockwise size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5E6D66]" />
            <Input
              placeholder="Note d'historique…"
              value={filterHistorySearch}
              onChange={(e) => setFilterHistorySearch(e.target.value)}
              className="w-[200px] pl-7"
              data-testid="filter-opp-history"
            />
          </div>
          <Button variant="ghost" onClick={resetFilters} className="text-[#5E6D66]" data-testid="filter-opp-reset">Réinitialiser</Button>
        </CardContent></Card>
      )}

      {/* KPIs + Répartition + Relances */}
      {(() => {
        const totalPipeline = opportunities.reduce((sum, o) => sum + (Number(o.montant_estime) || 0), 0);
        const convertis = opportunities.filter(o => o.pipeline_stage === 'converti').length;
        const perdus = opportunities.filter(o => o.pipeline_stage === 'perdu').length;
        const clotures = convertis + perdus;
        const tauxConversion = clotures > 0 ? (convertis / clotures) * 100 : 0;
        const staleOpps = opportunities.filter(isStaleOpp);
        const stageStats = pipelineStages.map(s => {
          const items = opportunities.filter(o => o.pipeline_stage === s.id);
          const montant = items.reduce((sum, o) => sum + (Number(o.montant_estime) || 0), 0);
          return { ...s, montant, count: items.length };
        });
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-[#E8E6E1]" data-testid="kpi-total-pipeline">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#2D5A4C]/10 flex items-center justify-center">
                    <ChartLine size={20} className="text-[#2D5A4C]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#5E6D66] uppercase tracking-wide">Total pipeline</p>
                    <p className="text-xl font-semibold text-[#1A2421] tabular-nums" data-testid="kpi-total-pipeline-value">
                      {totalPipeline.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-[#E8E6E1]" data-testid="kpi-taux-conversion">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#8DB0A1]/20 flex items-center justify-center">
                    <Target size={20} className="text-[#2D5A4C]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#5E6D66] uppercase tracking-wide">Taux de conversion</p>
                    <p className="text-xl font-semibold text-[#1A2421] tabular-nums" data-testid="kpi-taux-conversion-value">
                      {tauxConversion.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                    </p>
                    <p className="text-xs text-[#5E6D66]">{convertis} converti(s) / {clotures} clôturée(s)</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-[#E8E6E1]" data-testid="kpi-total-opps">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#E8A365]/20 flex items-center justify-center">
                    <CurrencyDollar size={20} className="text-[#D47A62]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#5E6D66] uppercase tracking-wide">Opportunités</p>
                    <p className="text-xl font-semibold text-[#1A2421] tabular-nums" data-testid="kpi-total-opps-value">
                      {opportunities.length}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Répartition des montants par étape */}
            {totalPipeline > 0 && (
              <Card className="border-[#E8E6E1]" data-testid="kpi-repartition-etapes">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-[#5E6D66] uppercase tracking-wide">Répartition des montants par étape</p>
                    <p className="text-xs text-[#5E6D66] tabular-nums">{totalPipeline.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
                  </div>
                  <div className="flex w-full h-3 rounded-full overflow-hidden bg-[#F1EFE9]">
                    {stageStats.map(s => {
                      const pct = totalPipeline > 0 ? (s.montant / totalPipeline) * 100 : 0;
                      if (pct <= 0) return null;
                      return (
                        <div
                          key={s.id}
                          style={{ width: `${pct}%`, backgroundColor: s.color }}
                          title={`${s.label} • ${s.montant.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € (${pct.toFixed(1)} %)`}
                          data-testid={`kpi-bar-${s.id}`}
                        />
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {stageStats.map(s => {
                      const pct = totalPipeline > 0 ? (s.montant / totalPipeline) * 100 : 0;
                      return (
                        <div key={s.id} className="flex items-center gap-1.5 text-xs text-[#5E6D66]">
                          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: s.color }} />
                          <span>{s.label}</span>
                          <span className="text-[#1A2421] tabular-nums font-medium">
                            {s.montant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                          </span>
                          <span className="text-[#5E6D66]">({pct.toFixed(1)} %)</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Alertes relance */}
            {staleOpps.length > 0 && (
              <Card className="border-[#E8A365]/50 bg-[#E8A365]/5" data-testid="stale-opps-alert">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#E8A365]/20 flex items-center justify-center flex-shrink-0">
                      <Bell size={20} className="text-[#D47A62]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1A2421]" data-testid="stale-opps-count">
                        {staleOpps.length} opportunité{staleOpps.length > 1 ? 's' : ''} à relancer
                      </p>
                      <p className="text-xs text-[#5E6D66] mb-2">Aucune activité depuis {STALE_DAYS} jours ou plus</p>
                      <div className="flex flex-wrap gap-1.5">
                        {staleOpps.slice(0, 8).map(o => {
                          const d = daysSince(o.updated_at || o.created_at);
                          return (
                            <button
                              key={o.id}
                              type="button"
                              onClick={() => handleCardClick(o)}
                              className="text-xs bg-white border border-[#E8A365]/40 hover:border-[#D47A62] rounded-md px-2 py-1 text-[#1A2421] transition-colors"
                              data-testid={`stale-opp-${o.id}`}
                            >
                              {o.titre} <span className="text-[#D47A62]">· {d}j</span>
                            </button>
                          );
                        })}
                        {staleOpps.length > 8 && (
                          <span className="text-xs text-[#5E6D66] px-2 py-1">+{staleOpps.length - 8} autres</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        );
      })()}

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {pipelineStages.map((stage) => {
          const stageOpps = opportunities.filter(o => o.pipeline_stage === stage.id);
          return (
            <div
              key={stage.id}
              className="flex-shrink-0 w-[300px]"
              onDrop={(e) => handleDrop(e, stage.id)}
              onDragOver={handleDragOver}
              data-testid={`pipeline-stage-${stage.id}`}
            >
              <Card className="border-[#E8E6E1] bg-[#F9F8F5]">
                <CardHeader className="p-3 pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }}></div>
                      <CardTitle className="text-sm font-semibold text-[#1A2421]">{stage.label}</CardTitle>
                    </div>
                    <Badge variant="outline" className="text-xs">{stageOpps.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-2 space-y-2 min-h-[400px]">
                  {stageOpps.map((opp) => (
                    <Card
                      key={opp.id}
                      className="border-[#E8E6E1] bg-white cursor-grab hover:shadow-md transition-shadow"
                      draggable
                      onDragStart={(e) => handleDragStart(e, opp)}
                      onClick={() => handleCardClick(opp)}
                      data-testid={`opp-card-${opp.id}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-medium text-sm text-[#1A2421] line-clamp-2">{opp.titre}</h3>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {isStaleOpp(opp) && (
                              <Bell size={14} className="text-[#D47A62]" title={`À relancer (${daysSince(opp.updated_at || opp.created_at)}j)`} data-testid={`opp-stale-badge-${opp.id}`} />
                            )}
                            <DotsSixVertical size={16} className="text-[#5E6D66]" />
                          </div>
                        </div>
                        
                        {opp.organization_id && (
                          <p className="text-xs text-[#5E6D66] mb-1 truncate">
                            {getOrgName(opp.organization_id)}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-2 flex-wrap mt-2">
                          {opp.montant_estime && (
                            <Badge variant="outline" className="text-xs flex items-center gap-1">
                              <CurrencyDollar size={12} />
                              {opp.montant_estime.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                            </Badge>
                          )}
                          {opp.type_besoin && (
                            <Badge className="text-xs bg-[#8DB0A1]/20 text-[#2D5A4C]">
                              {typeBesoinOptions.find(t => t.value === opp.type_besoin)?.label || opp.type_besoin}
                            </Badge>
                          )}
                        </div>
                        
                        {opp.contact_id && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-[#5E6D66]">
                            <User size={12} />
                            {getContactName(opp.contact_id)}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {editingOpp ? 'Modifier l\'opportunité' : 'Nouvelle opportunité'}
            </DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input
                value={formData.titre}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                required
                placeholder="Ex: Accompagnement création SCOP"
                data-testid="input-titre"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Organisation</Label>
                <Select value={formData.organization_id} onValueChange={(v) => setFormData({ ...formData, organization_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.raison_sociale}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Contact</Label>
                <Select value={formData.contact_id} onValueChange={(v) => setFormData({ ...formData, contact_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {contacts.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.prenom} {c.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type de besoin</Label>
                <Select value={formData.type_besoin} onValueChange={(v) => setFormData({ ...formData, type_besoin: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {typeBesoinOptions.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Étape pipeline</Label>
                <Select value={formData.pipeline_stage} onValueChange={(v) => setFormData({ ...formData, pipeline_stage: v })}>
                  <SelectTrigger data-testid="input-opp-stage">
                    <SelectValue>
                      <StageLabel stage={pipelineStages.find(s => s.id === formData.pipeline_stage)} />
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {pipelineStages.filter(s => s.id !== 'perdu').map(s => (
                      <SelectItem key={s.id} value={s.id} data-testid={`input-opp-stage-${s.id}`}><StageLabel stage={s} /></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Montant estimé</Label>
                <CurrencyInput
                  value={formData.montant_estime}
                  onChange={(n) => setFormData({ ...formData, montant_estime: n == null ? '' : n })}
                  currency="€"
                  testId="input-opp-montant"
                />
              </div>
              <div className="space-y-2">
                <Label>Probabilité (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.probabilite_conversion}
                  onChange={(e) => setFormData({ ...formData, probabilite_conversion: e.target.value })}
                  placeholder="50"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Notes de qualification</Label>
                <AIAssistButton
                  value={formData.notes_qualification}
                  field="Notes de qualification d'opportunité"
                  onApply={(t) => setFormData({ ...formData, notes_qualification: t })}
                  testId="ai-assist-opp-notes"
                />
              </div>
              <Textarea
                value={formData.notes_qualification}
                onChange={(e) => setFormData({ ...formData, notes_qualification: e.target.value })}
                rows={3}
                placeholder="Contexte, besoins identifiés, prochaines étapes..."
              />
            </div>

            {editingOpp && (
              <div className="space-y-2 pt-2 border-t border-[#E8E6E1]" data-testid="opp-history-section">
                <Label className="text-sm font-semibold text-[#1A2421]">Historique du pipeline</Label>
                {historyLoading ? (
                  <p className="text-xs text-[#5E6D66]">Chargement…</p>
                ) : history.length === 0 ? (
                  <p className="text-xs text-[#5E6D66]" data-testid="opp-history-empty">Aucun changement d'étape enregistré.</p>
                ) : (
                  <ol className="relative border-l-2 border-[#E8E6E1] ml-2 space-y-3 pt-1" data-testid="opp-history-list">
                    {history.map((h, i) => {
                      const from = pipelineStages.find(s => s.id === h.from_stage);
                      const to = pipelineStages.find(s => s.id === h.to_stage);
                      const d = h.changed_at ? new Date(h.changed_at) : null;
                      const dateStr = d ? d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '';
                      return (
                        <li key={i} className="ml-4 relative" data-testid={`opp-history-item-${i}`}>
                          <span
                            className="absolute -left-[22px] top-1 w-3 h-3 rounded-full ring-2 ring-white"
                            style={{ backgroundColor: to?.color || '#5E6D66' }}
                          />
                          <div className="flex items-center gap-2 flex-wrap text-xs">
                            {from ? (
                              <Badge variant="outline" className="text-[10px] gap-1" style={{ borderColor: from.color, color: from.color }}>
                                <from.icon size={11} />{from.label}
                              </Badge>
                            ) : (
                              <span className="text-[#5E6D66] text-[10px]">Création</span>
                            )}
                            <ArrowRight size={12} className="text-[#5E6D66]" />
                            <Badge className="text-[10px] text-white gap-1" style={{ backgroundColor: to?.color || '#5E6D66' }}>
                              {to?.icon && <to.icon size={11} />}{to?.label || h.to_stage}
                            </Badge>
                            <span className="text-[#5E6D66] tabular-nums">{dateStr}</span>
                          </div>
                          {h.note && (
                            <p className="text-xs italic text-[#5E6D66] mt-0.5 pl-1 border-l border-[#E8E6E1]" data-testid={`opp-history-note-${i}`}>
                              « {h.note} »
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-opp-btn">
                {editingOpp ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lost Reason Dialog */}
      <Dialog open={lostDialogOpen} onOpenChange={setLostDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Motif de perte</DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#5E6D66]">
              Pourquoi cette opportunité est-elle perdue ?
            </p>
            <Select value={lostReason} onValueChange={setLostReason}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un motif" />
              </SelectTrigger>
              <SelectContent>
                {motifPerteOptions.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#5E6D66]">Note (optionnelle)</Label>
              <Textarea
                value={stageChangeNote}
                onChange={(e) => setStageChangeNote(e.target.value)}
                rows={2}
                placeholder="Précisions sur la perte…"
                data-testid="stage-change-note-lost"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setLostDialogOpen(false); setStageChangeNote(''); }}>
              Annuler
            </Button>
            <Button
              onClick={handleLostConfirm}
              disabled={!lostReason}
              className="bg-[#D47A62] hover:bg-[#B86650]"
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stage Change Note Dialog */}
      <Dialog open={stageChangeOpen} onOpenChange={setStageChangeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Déplacer « {stageChangeState?.opp?.titre} »
            </DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          {stageChangeState && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                {(() => {
                  const from = pipelineStages.find(s => s.id === stageChangeState.fromStage);
                  const to = pipelineStages.find(s => s.id === stageChangeState.toStage);
                  return (
                    <>
                      <Badge variant="outline" className="gap-1" style={{ borderColor: from?.color, color: from?.color }}>
                        {from?.icon && <from.icon size={12} />}{from?.label}
                      </Badge>
                      <ArrowRight size={16} className="text-[#5E6D66]" />
                      <Badge className="text-white gap-1" style={{ backgroundColor: to?.color }}>
                        {to?.icon && <to.icon size={12} />}{to?.label}
                      </Badge>
                    </>
                  );
                })()}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5E6D66]">Note (optionnelle)</Label>
                <Textarea
                  value={stageChangeNote}
                  onChange={(e) => setStageChangeNote(e.target.value)}
                  rows={3}
                  placeholder="Ex: Réunion client prévue, demande de devis en attente…"
                  data-testid="stage-change-note-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setStageChangeOpen(false); setStageChangeState(null); setStageChangeNote(''); }}>
              Annuler
            </Button>
            <Button
              variant="outline"
              onClick={() => { setStageChangeNote(''); handleStageChangeConfirm(); }}
              data-testid="stage-change-confirm-empty"
            >
              Sans note
            </Button>
            <Button
              onClick={handleStageChangeConfirm}
              className="bg-[#2D5A4C] hover:bg-[#1E4136]"
              data-testid="stage-change-confirm"
            >
              Valider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
